import React, { useState } from 'react';
import { PnevmatikaFilters } from '../../types/pnevmatika';
import { Button } from '../common/Button';
import './PnevmatikaFilters.css';

interface PnevmatikaFiltersProps {
  filters: PnevmatikaFilters;
  onFiltersChange: (filters: PnevmatikaFilters) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  loading?: boolean;
}

export const PnevmatikaFiltersComponent: React.FC<PnevmatikaFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onResetFilters,
  loading = false,
}) => {
  // Local UI state for filters (keeps radio selections stable)
  const [localFilters, setLocalFilters] = useState<PnevmatikaFilters>(filters);
  const [isExpanded, setIsExpanded] = useState(false);

  // Note: we do not mirror parent filters back into local state to avoid
  // resetting UI-only values (like 'sezona' and 'z_platisci' radios) after apply.

  const handleInputChange = (field: keyof PnevmatikaFilters, value: string) => {
    // 1) Update local UI state
    const nextLocal: PnevmatikaFilters = { ...localFilters } as any;
    if (value === '') {
      delete (nextLocal as any)[field];
    } else if (
      field === 'oznaka_gume' ||
      field === 'oznaka_gume_min' ||
      field === 'oznaka_gume_max'
    ) {
      const numValue = parseInt(value);
      if (isNaN(numValue)) delete (nextLocal as any)[field];
      else (nextLocal as any)[field] = numValue;
    } else if (field === 'overdue') {
      // Checkbox maps to boolean in local state
      (nextLocal as any).overdue = value === '1';
    } else {
      (nextLocal as any)[field] = value;
    }
    setLocalFilters(nextLocal);

    // 2) Map UI-only fields to API params and auto-apply
    const mapped: PnevmatikaFilters = { ...nextLocal } as any;
    // Ensure overdue is a proper boolean for API
    if ((nextLocal as any).overdue !== true) {
      delete (mapped as any).overdue;
    }
  // Booleans and UI-to-API mapping
    if ((nextLocal as any).z_platisci === 'da') (mapped as any).z_platisci = true;
    else if ((nextLocal as any).z_platisci === 'ne') (mapped as any).z_platisci = false;
    else delete (mapped as any).z_platisci;

    if ((nextLocal as any).sezona === 'letna') (mapped as any).letna = true;
    else if ((nextLocal as any).sezona === 'zimska') (mapped as any).letna = false;
    else delete (mapped as any).letna;

    delete (mapped as any).sezona;

    onFiltersChange(mapped);
    onApplyFilters();
  };

  const handleReset = () => {
    setLocalFilters({});
    onResetFilters();
  };

  const handleKeyPress = (_e: React.KeyboardEvent) => {
    // No-op; auto-applies on each change
  };

  const hasActiveFilters = Object.keys(localFilters).length > 0;

  return (
    <div className="pnevmatika-filters">
      <div className="filters-header">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setIsExpanded(!isExpanded)}
          className="filters-toggle"
        >
          {isExpanded ? 'Skrij filtre' : 'Prikaži filtre'} 
          {hasActiveFilters && <span className="active-indicator">●</span>}
        </Button>
        
        {hasActiveFilters && (
          <span className="active-filters-count">
            {Object.keys(localFilters).length} aktivnih filtrov
          </span>
        )}
      </div>

      {isExpanded && (
        <div className="filters-content">
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="lastnik">Lastnik (ime ali priimek)</label>
              <input
                type="text"
                id="lastnik"
                value={localFilters.lastnik || ''}
                onChange={(e) => handleInputChange('lastnik', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Išči po imenu ali priimku..."
              />
            </div>

            <div className="filter-group">
              <label htmlFor="telefonska_stevilka">Telefonska številka</label>
              <input
                type="text"
                id="telefonska_stevilka"
                value={localFilters.telefonska_stevilka || ''}
                inputMode="numeric"
                pattern="[0-9]*"
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, '');
                  handleInputChange('telefonska_stevilka', digitsOnly);
                }}
                onKeyPress={handleKeyPress}
                placeholder="Delno ali polno število..."
              />
            </div>

            <div className="filter-group">
              <label htmlFor="oznaka_gume">Oznaka pnevmatike</label>
              <input
                type="number"
                id="oznaka_gume"
                value={localFilters.oznaka_gume || ''}
                onChange={(e) => handleInputChange('oznaka_gume', e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Natančna oznaka..."
                min="0"
              />
            </div>

            <div className="filter-group">
              <label htmlFor="spravljeno_od_from">Spravljeno od (od datuma)</label>
              <input
                type="date"
                id="spravljeno_od_from"
                value={localFilters.spravljeno_od_from || ''}
                onChange={(e) => handleInputChange('spravljeno_od_from', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="spravljeno_od_to">Spravljeno od (do datuma)</label>
              <input
                type="date"
                id="spravljeno_od_to"
                value={localFilters.spravljeno_od_to || ''}
                onChange={(e) => handleInputChange('spravljeno_od_to', e.target.value)}
              />
            </div>
          </div>

          <div className="filters-grid">
            <div className="filter-group">
              <label>Z platišči</label>
              <div className="radio-group">
                <label><input type="radio" name="z_platisci" checked={localFilters.z_platisci === 'da'} onChange={() => handleInputChange('z_platisci', 'da')} /> Da</label>
                <label><input type="radio" name="z_platisci" checked={localFilters.z_platisci === 'ne'} onChange={() => handleInputChange('z_platisci', 'ne')} /> Ne</label>
                <label><input type="radio" name="z_platisci" checked={localFilters.z_platisci !== 'da' && localFilters.z_platisci !== 'ne'} onChange={() => handleInputChange('z_platisci', '')} /> Vse</label>
              </div>
            </div>

            <div className="filter-group">
              <label>Tip</label>
              <div className="radio-group">
                <label><input type="radio" name="sezona" checked={localFilters.sezona === 'letna'} onChange={() => handleInputChange('sezona', 'letna')} /> Letna</label>
                <label><input type="radio" name="sezona" checked={localFilters.sezona === 'zimska'} onChange={() => handleInputChange('sezona', 'zimska')} /> Zimska</label>
                <label><input type="radio" name="sezona" checked={localFilters.sezona !== 'letna' && localFilters.sezona !== 'zimska'} onChange={() => handleInputChange('sezona', '')} /> Vse</label>
              </div>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={!!localFilters.overdue}
                  onChange={(e) => handleInputChange('overdue', e.target.checked ? '1' : '')}
                />{' '}
                Prekoračen čas hrambe (&gt; 1,5 leta)
              </label>
            </div>
          </div>

          <div className="filters-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={handleReset}
              disabled={loading}
            >
              Počisti filtre
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
